import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { EcpWorkflowEnvironmentService } from '../environment/environment.service';
import { firstValueFrom, Observable } from 'rxjs';

import { Info, RequestHeaderConst, RouterUrlConstant } from '../../constant/information.const';

import { StorageWrapperService } from '../storage-wrapper/storage-wrapper.service';
import { UserService } from '../user/user.service';
import { ApiParam } from '../../model/api-param';
import { RuntimeConfigService } from '@app/service/runtime-config.service';
import { isPlatformBrowser } from '@angular/common';
import { SawDust } from '@cudirect/sawdust/sawdust';

/**
 *
 * API Service
 * @export
 * @ class ApiService
 */
@Injectable({ providedIn: 'root' })
export class ApiService {
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly logger = inject(SawDust);

  constructor(
    private readonly httpClient: HttpClient,
    private readonly storageWrapperService: StorageWrapperService,
    private readonly userService: UserService,
    private readonly environmentService: EcpWorkflowEnvironmentService,
    private readonly runtimeConfigService: RuntimeConfigService
  ) {}

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private mergeHeader(hostName: string, header: any, url: string): HttpHeaders {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const preHeader = this.getHeaders(this.environmentService.ecpWorkflowEnvironment, hostName) as any;
    const headerObject = {};
    const cachedToken = this.userService.token;

    if (cachedToken) {
      headerObject[RequestHeaderConst.authorization] = `${cachedToken}`;
      // Temporarily duplicating session guid. Authorization will eventually become the JWT
      headerObject[RequestHeaderConst.dxSessionId] = `${cachedToken}`;
    }

    if (url === RouterUrlConstant.apiLoginUrl) {
      delete preHeader.Authorization;
      delete preHeader['Session-Id'];
    }

    for (const key in preHeader) {
      if (preHeader.hasOwnProperty(key)) {
        const element = preHeader[key];
        headerObject[key] = element;
      }
    }

    for (const key in header) {
      if (header.hasOwnProperty(key)) {
        const element = header[key];
        if (element !== undefined) {
          headerObject[key] = element;
        } else if (headerObject.hasOwnProperty(key)) {
          delete headerObject[key];
        }
      }
    }

    return new HttpHeaders(headerObject);
  }

  public async requestApi<T>({ urlObj, uiElement, body, queryParam, urlIds, callback, errorCallback }: ApiParam): Promise<T> {
    return (
      firstValueFrom(this.request<T>({ urlObj, uiElement, body, queryParam, urlIds }))
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .then((result: any) => {
          if (callback) {
            callback(result);
          }

          return result;
        })
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .catch((errorResponse: any) => {
          if (errorCallback) {
            errorCallback(errorResponse);
          }
          throw errorResponse;
        })
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public request<T>({ urlObj, uiElement, body, queryParam, urlIds: urlId }: ApiParam): Observable<T> {
    const queryParams = queryParam || {};
    const url = this.GetFullURL(urlObj.hostName, urlObj.url, urlId);
    urlObj.method = urlObj.method || RequestHeaderConst.methodType;
    let params: HttpParams;

    if (Object.keys(queryParams).length > 0) {
      // need to update queryParams type in api-param -- queryParam?: {[param: string]: string | readonly string[]};
      params = new HttpParams({ fromObject: queryParams });
    }

    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }
    const headers = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);

    // if (urlObj.url === RouterUrlConstant.login) {
    //   headers.delete(RequestHeaderConst.authorization);
    // }

    return this.httpClient.request(urlObj.method, url, {
      body,
      headers,
      params
    }) as Observable<T>;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public getImage({ urlObj, uiElement, body, queryParam, urlIds: urlId }: ApiParam): Observable<Blob> {
    const url = this.GetFullURL(urlObj.hostName, urlObj.url, urlId);

    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }

    const headers = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);

    return this.httpClient.get(url, {
      headers,
      responseType: 'blob'
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public postImage({ urlObj, uiElement, body, queryParam, urlIds: urlId }: ApiParam): Observable<Blob> {
    const url = this.GetFullURL(urlObj.hostName, urlObj.url, urlId);

    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }

    const headers = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);

    return this.httpClient.post(url, body, {
      headers,
      responseType: 'blob'
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  public downloadFile({ urlObj, uiElement, body, queryParam, urlIds: urlId }: ApiParam): Observable<Blob> {
    const url = this.GetFullURL(urlObj.hostName, urlObj.url, urlId);

    if (urlObj.defaultHeaders === undefined) {
      urlObj.defaultHeaders = {};
    }

    const headers = this.mergeHeader(urlObj.hostName, urlObj.defaultHeaders, urlObj.url);

    return this.httpClient.get(url, {
      headers,
      responseType: 'blob'
    });
  }

  /** Functiton to return full url */
  public GetFullURL = (
    objName: string,
    url: string,
    urlObj?: {
      [header: string]: string;
    }
  ): string => {
    const apiData = this.runtimeConfigService.getConfig?.[objName];
    const user = this.userService.user;
    let fullURL = url;
    const appNumber = this.storageWrapperService.get<string>(Info.appNumber);
    if (appNumber) {
      fullURL = fullURL.replace(/{{appNumber}}/gi, appNumber);
      fullURL = fullURL.replace(/{{loanNumber}}/gi, appNumber);
    }
    if (user) {
      if (user.qFileId) {
        fullURL = fullURL.replace(/{{QFileID}}/gi, user.qFileId);
      }
      if (user.dataSourceId) {
        fullURL = fullURL.replace(/{{dataSourceId}}/gi, user.dataSourceId);
      }
      if (user.borrowerId) {
        fullURL = fullURL.replace(/{{borrowerId}}/gi, user.borrowerId);
      }
      if (user.userID) {
        fullURL = fullURL.replace(/{{memberId}}/gi, user.userID.toString());
      }
      if (user.loanID) {
        fullURL = fullURL.replace(/{{loanId}}/gi, user.loanID.toString());
      }
    }

    const isHostMissing = apiData === undefined;
    if (isHostMissing) {
      if (this.isBrowser) {
        throw Error(Info.hostNotDefined);
      }
      this.logger.warn(`[SSR] Host data not defined for "${objName}" while constructing URL for "${url}".`, { component: 'ApiService' });
    }

    if (urlObj !== undefined) {
      for (const key in urlObj) {
        if (urlObj.hasOwnProperty(key)) {
          const element: string = urlObj[key];
          const urlKey = new RegExp(`{${key}}`, 'g');
          if (fullURL.indexOf(`{${key}}`) > 0) {
            fullURL = fullURL.replace(urlKey, element);
          }
        }
      }
    }

    const baseUrl = this.runtimeConfigService.getDXAPIBaseUrl();
    if (!baseUrl) {
      if (!this.isBrowser) {
        this.logger.warn(`[SSR] DX API base URL missing while constructing URL for "${url}".`, { component: 'ApiService' });
        return fullURL;
      }
      throw new Error('DX API base URL is not configured.');
    }

    return `${baseUrl}/${fullURL}`;
  };

  /**
   * IE compatible method to convert blob to string
   * @param blob input blob
   * @param callback fn to utilize the result of blob reading
   * @param errorHandler optional handler for blob reading failure
   */
  public blobToString(blob: Blob, callback: (result: string) => void, errorHandler?: () => void): void {
    const reader = new FileReader();
    // triggered once blob done loading
    reader.addEventListener('loadend', () => {
      if (reader && reader.result) {
        // eslint-disable-next-line @typescript-eslint/no-base-to-string
        callback(reader.result.toString());
      } else {
        errorHandler();
      }
    });
    reader.readAsText(blob);
  }

  /**
   * Set temporary headers, do we even need a method for this?
   * @param environment
   * @param hostName
   * @returns - temporary headers
   */
  public getHeaders = (environment, hostName: string): object => {
    const apiData = environment?.[hostName];

    if (apiData === undefined) {
      if (!this.isBrowser) {
        this.logger.warn(`[SSR] Host data not defined for "${hostName}". Returning empty headers.`, { component: 'ApiService' });
        return {};
      }
      throw Error('Host data not defined');
    }

    if (apiData.headers === undefined) {
      return {};
    }
    const tempHeader = {};
    Object.assign(tempHeader, apiData.headers);

    return tempHeader;
  };
}
